import React, { lazy } from 'react';

import useContentAuthGate from '../hooks/useContentAuthGate';
import { ITemplateProps } from '../types';

import AuthorCard from '../blocks/AuthorCard';
import AuthSection from '../blocks/AuthSection';
import ContentCta from '../blocks/ContentCta';
import Separator from '../blocks/Separator';
import ShareBar from '../blocks/ShareBar';
import AccountBlock from '../compositions/AccountBlock';
import SkyMoment from '../compositions/SkyMoment';
import StoreAndSearch from '../compositions/StoreAndSearch';
import ChromeBoundary from '../primitives/ChromeBoundary';
import DeferredBlock from '../primitives/DeferredBlock';
import AuthSlot from '../slots/AuthSlot';
import ContentBody from '../slots/ContentBody';
import ContentHeader from '../slots/ContentHeader';
import CtaSlot from '../slots/CtaSlot';
import LeftSection from '../slots/LeftSection';
import RightSection from '../slots/RightSection';
import ShareSlot from '../slots/ShareSlot';

const CategoriesTree = lazy(() => import('../blocks/CategoriesTree'));
const RelatedArticles = lazy(() => import('../blocks/RelatedArticles'));

const ArticleTemplate: React.FC<ITemplateProps> = ({ data, head, body }) => {
    useContentAuthGate({ isLogged: data.isLogged, authStep: data.authStep });

    return (
        <>
            <ContentHeader content={head} />

            <CtaSlot>
                {data.cta && (
                    <ChromeBoundary region="cta">
                        <ContentCta step={data.authStep} title={data.cta.title} label={data.cta.label} />
                    </ChromeBoundary>
                )}
            </CtaSlot>

            <ContentBody content={body} />

            <ShareSlot>
                {data.share && (
                    <ChromeBoundary region="share">
                        <ShareBar label={data.share.label} hrefs={data.share.hrefs} />
                    </ChromeBoundary>
                )}
            </ShareSlot>

            <LeftSection>
                <Separator opening />

                {data.author && (
                    <ChromeBoundary region="author">
                        <AuthorCard author={data.author} />
                    </ChromeBoundary>
                )}
            </LeftSection>

            <RightSection>
                <StoreAndSearch data={data} />

                <AccountBlock data={data} />

                {data.categories && (
                    <DeferredBlock
                        region="categories"
                        className="content-block content-block--categories"
                        placeholderHeight={420}
                    >
                        <CategoriesTree title={data.categories.title} items={data.categories.items} />
                    </DeferredBlock>
                )}

                {data.related && (
                    <DeferredBlock
                        region="related-articles"
                        className="content-block content-block--related-articles"
                        placeholderHeight={1200}
                    >
                        <RelatedArticles
                            title={data.related.title}
                            readMoreLabel={data.related.readMoreLabel}
                            items={data.related.items}
                        />
                    </DeferredBlock>
                )}

                <SkyMoment data={data} />
            </RightSection>

            <AuthSlot hasForm={!!data.authSection}>
                {data.authSection && (
                    <ChromeBoundary region="auth">
                        <AuthSection
                            title={data.authSection.title}
                            description={data.authSection.description}
                            authStep={data.authStep}
                            maintenance={data.maintenance}
                        />
                    </ChromeBoundary>
                )}
            </AuthSlot>
        </>
    );
};

export default ArticleTemplate;
